import { html, nothing, type TemplateResult } from 'lit';

/**
 * The handful of shapes this app repeats everywhere.
 *
 * Each of these existed in four or five slightly different spellings — the dashed
 * placeholder alternated between `p-4`/`p-6` and `text-sm`/`text-base`, the section
 * spinner appeared at four different sizes for the same "still loading" state, and each
 * of the three dialogs built its own title bar. None of that was a decision; it is what
 * happens when a pattern is copied rather than named. They are named here so the next
 * one is a call rather than another copy.
 */

/** "There is nothing here yet" — a dashed outline rather than empty space. */
export const emptyState = (message: string | TemplateResult, options?: { compact?: boolean }): TemplateResult => html`
  <div
    class="rounded-box border-base-300 text-base-content/60 ${options?.compact
      ? 'p-4'
      : 'p-6'} border border-dashed text-center text-sm"
  >
    ${message}
  </div>
`;

/** A section is still loading. One size, so two sections loading side by side agree. */
export const sectionLoader = (options?: { compact?: boolean }): TemplateResult => html`
  <div class="${options?.compact ? 'py-4' : 'py-8'} flex justify-center">
    <span class="loading loading-spinner loading-md text-primary"></span>
  </div>
`;

/** A query failed. Kept quiet and inline — the surrounding content usually still stands. */
export const errorText = (message: string): TemplateResult => html`
  <p class="text-error shrink-0 text-sm">${message}</p>
`;

/**
 * A dialog's title bar.
 *
 * `close` is optional: the organization picker resolves through its own Cancel button and
 * would otherwise offer two ways out of the same modal, one of which skips its handler.
 */
export const dialogHeader = (options: {
  title: string;
  description?: string;
  close?: () => void;
}): TemplateResult => html`
  <div class="border-base-300 flex shrink-0 items-start justify-between gap-4 border-b pb-4">
    <div class="flex min-w-0 flex-col gap-0.5">
      <h3 class="text-base-content text-lg leading-tight font-semibold">${options.title}</h3>
      ${options.description ? html`<p class="text-base-content/60 text-sm">${options.description}</p>` : nothing}
    </div>
    ${options.close
      ? html`<button
          type="button"
          class="btn btn-ghost btn-sm btn-square text-base-content shrink-0"
          aria-label="Close dialog"
          @click=${options.close}
        >
          <span class="icon-[material-symbols--close-rounded] text-xl"></span>
        </button>`
      : nothing}
  </div>
`;
